"use client";
import type { DateRange } from "@cmp-src/calendar/types";
import { CalendarInput } from "@cmp-src/form/calendar/CalendarInput/CalendarInput";
import { Modal } from "@cmp-src/overlay/modals/Modal";
import { Button } from "@cmp-src/primitives/button";
import type { ISchedule } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { getDeviceTZ } from "@gettactic/helpers/src/intl/getDeviceTZ";
import { getCurrentUserCanCancel } from "@gettactic/helpers/src/resources/getCurrentUserCanCancel";
import {
  useAuthenticated,
  useDedicatedResourceOf,
  useMySchedules
} from "@gettactic/hooks";
import { addDays, format } from "date-fns";
import React from "react";
import {
  isReleaseDeskSelectionComplete,
  KEEP_DESK_SELECTION,
  type ReleaseDeskSelection,
  useReleaseDedicatedDesk
} from "@/features/reserve/hooks/useReleaseDedicatedDesk";
import { useCustomParkingVerbiageOnWeb } from "@/lib/hooks/useCustomParkingVerbiage";
import { ReservationCancel } from "../ReservationCancel/ReservationCancel";
import { CancelReservationsDay } from "./CancelReservationsDay";
import { getReservationCategoryLabel } from "./categoryLabels";
import {
  getReservationCategory,
  groupReservations,
  RESERVATION_CATEGORY_ORDER,
  type ReservationCategory
} from "./groupReservations";
import { ReleaseDeskOption } from "./ReleaseDeskOption";

const DATE_FORMAT = "yyyy-MM-dd";
/** Default window: today through two weeks out — a typical time-off stretch. */
const DEFAULT_RANGE_DAYS = 14;

export interface CancelReservationsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Bulk review-and-cancel of the signed-in user's own reservations.
 *
 * Pick a date range, see everything grouped by day and by category, then
 * cancel the lot, a whole category, or an individual booking — the flow people
 * need when a holiday lands on top of a month of standing desk bookings.
 */
export const CancelReservationsModal: React.FC<
  CancelReservationsModalProps
> = ({ isOpen, onClose }) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const parkingVerbiage = useCustomParkingVerbiageOnWeb();
  const timeZone = getDeviceTZ() || "UTC";

  const getDefaultRange = (): DateRange => {
    const today = new Date();
    return { from: today, to: addDays(today, DEFAULT_RANGE_DAYS) };
  };

  const [range, setRange] = React.useState<DateRange>(getDefaultRange);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  // Snapshot of everything "Cancel N reservations" showed at the moment it
  // was clicked: the reservations, what was chosen for the desk, and the days
  // both were read for.
  //
  // All of it is live otherwise, and all of it can move while the confirm
  // dialog is up. selectedSchedules is rebuilt by any background refetch
  // (window focus, staleTime elapsing), and ReservationCancel is bound to
  // whatever it is passed at render time, so confirming that could "succeed"
  // against zero schedules while claiming everything was cancelled. The range
  // picker and the release toggle stay interactive behind the dialog, so the
  // days can be changed after the reservations to cancel were chosen - which
  // freed the desk for days nobody reviewed. One object, taken whole, is what
  // gets sent.
  const [confirmation, setConfirmation] = React.useState<{
    schedules: ISchedule[];
    selection: ReleaseDeskSelection;
    startsOn: string;
    endsOn: string;
  } | null>(null);

  // This modal is mounted once in the sidebar for the life of the page, not
  // per-open — the mount-time `useState` initializer above only runs once, so
  // without this a range picked up on page load in the morning would still
  // read "today" that evening. Resetting on every open keeps "today" current.
  React.useEffect(() => {
    if (isOpen) {
      setRange(getDefaultRange());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const start = range.from ? format(range.from, DATE_FORMAT) : "";
  const end = range.to ? format(range.to, DATE_FORMAT) : start;

  // People with a dedicated desk can free it for the same days, so it is
  // not sitting empty while they are away.
  const userId = authenticatedUser.user?.id;
  const { data: myDesk } = useDedicatedResourceOf(isOpen ? userId : undefined);
  const [releaseSelection, setReleaseSelection] =
    React.useState<ReleaseDeskSelection>(KEEP_DESK_SELECTION);
  const { release, isReleasing } = useReleaseDedicatedDesk();
  const isReleasingDesk = !!myDesk && releaseSelection.choice !== "keep";

  // The choice and the days are handed in rather than read live: the range
  // picker and the release toggle both stay interactive behind the cancel
  // confirmation, and neither a toggle nor a new range in between may change
  // what the button said it would do.
  const releaseMyDesk = React.useCallback(
    async (
      selection: ReleaseDeskSelection,
      startsOn: string,
      endsOn: string
    ) => {
      if (!myDesk || !startsOn || selection.choice === "keep") return;
      const released = await release({
        deskId: myDesk.id,
        deskName: myDesk.name,
        // Days picked on the viewer's calendar; the release holds them to
        // the desk office's.
        officeId: myDesk.office_id,
        startsOn,
        endsOn,
        selection,
        reason: "Time off"
      });
      if (released) {
        setReleaseSelection(KEEP_DESK_SELECTION);
      }
    },
    [myDesk, release]
  );

  const {
    data: mySchedules,
    isLoading,
    // Any in-flight fetch, not just the first: React Query serves a cached
    // range with `isLoading` false, and confirming against that would cancel
    // an incomplete set while looking complete.
    isFetching,
    error: loadError
  } = useMySchedules({
    start,
    end,
    tz: timeZone,
    enabled: isOpen && !!start
  });

  const groups = React.useMemo(
    () => groupReservations(mySchedules?.schedules ?? []),
    [mySchedules]
  );
  const isTruncated = !!mySchedules?.isTruncated;
  // Nothing may be selected while the list is untrustworthy: cancelling from a
  // failed or partial fetch would drop some reservations and silently keep
  // others.
  const isBlocked = !!loadError || isTruncated;

  // Past and non-cancellable reservations still show — hiding them would make
  // the list disagree with the agenda — but they cannot be selected.
  const lockedIds = React.useMemo(() => {
    const locked = new Set<string>();
    for (const group of groups) {
      for (const schedule of group.schedules) {
        if (!getCurrentUserCanCancel(schedule, authenticatedUser)) {
          locked.add(schedule.id);
        }
      }
    }
    return locked;
  }, [groups, authenticatedUser]);

  const selectableSchedules = React.useMemo(
    () =>
      isBlocked
        ? []
        : groups
            .flatMap((group) => group.schedules)
            .filter((schedule) => !lockedIds.has(schedule.id)),
    [groups, lockedIds, isBlocked]
  );

  // Selections are keyed by schedule id, so a range change can leave ids
  // behind that are no longer on screen — drop them rather than cancelling
  // something the user can no longer see.
  React.useEffect(() => {
    setSelectedIds((current) => {
      if (current.size === 0) return current;
      const visible = new Set(selectableSchedules.map((s) => s.id));
      const next = new Set([...current].filter((id) => visible.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [selectableSchedules]);

  const toggleOne = React.useCallback((schedule: ISchedule) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(schedule.id)) {
        next.delete(schedule.id);
      } else {
        next.add(schedule.id);
      }
      return next;
    });
  }, []);

  const toggleMany = React.useCallback(
    (targets: ISchedule[], selected: boolean) => {
      setSelectedIds((current) => {
        const next = new Set(current);
        for (const schedule of targets) {
          if (selected) {
            next.add(schedule.id);
          } else {
            next.delete(schedule.id);
          }
        }
        return next;
      });
    },
    []
  );

  const categoryCounts = React.useMemo(() => {
    const counts = new Map<ReservationCategory, ISchedule[]>();
    for (const schedule of selectableSchedules) {
      const category = getReservationCategory(schedule);
      const existing = counts.get(category);
      if (existing) {
        existing.push(schedule);
      } else {
        counts.set(category, [schedule]);
      }
    }
    return counts;
  }, [selectableSchedules]);

  const selectedSchedules = React.useMemo(
    () => selectableSchedules.filter((s) => selectedIds.has(s.id)),
    [selectableSchedules, selectedIds]
  );

  const areAllSelected =
    selectableSchedules.length > 0 &&
    selectedIds.size === selectableSchedules.length;

  const handleClose = () => {
    // Not while the cancel confirmation is up: it is a question about this
    // modal's own action, and clearing the snapshot under it would drop the
    // desk release that was confirmed along with the cancellations.
    if (isConfirmOpen) {
      return;
    }
    setSelectedIds(new Set());
    setConfirmation(null);
    setReleaseSelection(KEEP_DESK_SELECTION);
    onClose();
  };

  const selectedCount = selectedSchedules.length;
  const primaryLabel =
    selectedCount > 0
      ? `Cancel ${selectedCount} reservation${selectedCount === 1 ? "" : "s"}${
          isReleasingDesk ? " and release desk" : ""
        }`
      : isReleasingDesk
        ? "Release desk"
        : "Cancel reservations";

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Cancel reservations"
        description="Pick a date range, then cancel everything, a whole category, or just the ones you choose."
        size="lg"
      >
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <CalendarInput
              label="Dates"
              mode="range"
              variant="outlined"
              setValueOnApply
              selectedDate={range}
              onSelect={(value) => {
                if (
                  value &&
                  !Array.isArray(value) &&
                  !(value instanceof Date)
                ) {
                  setRange(value);
                }
              }}
            />

            {selectableSchedules.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    toggleMany(selectableSchedules, !areAllSelected)
                  }
                >
                  {areAllSelected ? "Clear all" : "Select all"}
                </Button>
                {RESERVATION_CATEGORY_ORDER.filter((category) =>
                  categoryCounts.has(category)
                ).map((category) => {
                  const categorySchedules = categoryCounts.get(category) ?? [];
                  const isFullySelected = categorySchedules.every((schedule) =>
                    selectedIds.has(schedule.id)
                  );
                  return (
                    <Button
                      key={category}
                      type="button"
                      variant="outline"
                      size="sm"
                      className={cn(isFullySelected && "border-primary")}
                      onClick={() =>
                        toggleMany(categorySchedules, !isFullySelected)
                      }
                    >
                      {getReservationCategoryLabel(category, parkingVerbiage)}
                      <span className="ml-1 text-neutral-500">
                        {categorySchedules.length}
                      </span>
                    </Button>
                  );
                })}
              </div>
            )}

            {myDesk && (
              <ReleaseDeskOption
                deskName={myDesk.name}
                selection={releaseSelection}
                onSelectionChange={setReleaseSelection}
              />
            )}

            <div className="max-h-[24rem] overflow-y-auto">
              {isLoading ? (
                <p className="py-8 text-center text-neutral-500 text-sm">
                  Loading your reservations…
                </p>
              ) : loadError ? (
                <p className="py-8 text-center text-destructive text-sm">
                  Couldn't load your reservations. Close this and try again.
                </p>
              ) : isTruncated ? (
                <p className="py-8 text-center text-destructive text-sm">
                  You have too many reservations in this range to list. Pick a
                  shorter date range.
                </p>
              ) : groups.length === 0 ? (
                <p className="py-8 text-center text-neutral-500 text-sm">
                  No reservations in this date range.
                </p>
              ) : (
                groups.map((group) => (
                  <CancelReservationsDay
                    key={group.date}
                    group={group}
                    selectedIds={selectedIds}
                    lockedIds={lockedIds}
                    onToggle={toggleOne}
                    onToggleMany={toggleMany}
                    parkingVerbiage={parkingVerbiage}
                  />
                ))
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="outline" onClick={handleClose}>
            Close
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={
              (selectedCount === 0 && !isReleasingDesk) ||
              !isReleaseDeskSelectionComplete(releaseSelection) ||
              isFetching ||
              isReleasing
            }
            onClick={() => {
              // Nothing to cancel: releasing the desk is the whole action,
              // and it happens now, so the live days are the right ones.
              if (selectedCount === 0) {
                void releaseMyDesk(releaseSelection, start, end);
                return;
              }
              // Snapshot now, not a moment later: nothing between this click
              // and the confirm below should be able to change what gets
              // cancelled, what happens to the desk, or for which days.
              setConfirmation({
                schedules: selectedSchedules,
                selection: releaseSelection,
                startsOn: start,
                endsOn: end
              });
              setIsConfirmOpen(true);
            }}
          >
            {primaryLabel}
          </Button>
        </Modal.Footer>
      </Modal>

      <ReservationCancel
        open={isConfirmOpen}
        setOpen={setIsConfirmOpen}
        schedules={confirmation?.schedules ?? []}
        onCancelSuccess={() => {
          setSelectedIds(new Set());
          // The desk is freed once the reservations are gone, so the release
          // never has to cancel anything the person did not review here.
          if (confirmation) {
            void releaseMyDesk(
              confirmation.selection,
              confirmation.startsOn,
              confirmation.endsOn
            );
          }
        }}
      />
    </>
  );
};
